import React, { useEffect, useState } from "react";
import s from "./Recipient.module.scss";
import { UsersApiResponse } from "../../../../../../libs/Utils/Types/User";
import useSWR from "swr";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import { ThreeDots } from "react-loader-spinner";
import clsx from "clsx";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import Tags from "../../../../../../global components/Tags/Tags";
import {
  PromoCode,
  
} from "../../../../../../libs/Utils/Types/PromoCode";

type Prop = {
  handleUsers: (users: string[]) => void;
  handleDrawer: () => void;
  handleTags: (updatedTags: { key: string; label: string }[]) => void;
  codeId: string;
};
function Recipients({ handleUsers, handleDrawer, handleTags, codeId }: Prop) {
  const token = UseAuthToken();
  const [newsUpdates, setNewsUpdates] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [tags, setTags] = useState<{ key: string; label: string }[]>([]);
  const {
    data,
    isLoading: loading,
  } = useSWR<PromoCode, any, any>(
    token ? [`api/admin/promo-codes/${codeId}`, token] : null,
    swrFetcher,
    {
      isPaused: () => !codeId,
    }
  );
  const {
    data: TableUsers,
    isLoading,
    error,
  } = useSWR<UsersApiResponse>(
    token
      ? [
          "api/admin/promo-codes/users",
          token,
          {
            page: 1,
            limit: 50,
            search: searchValue,
          },
        ]
      : null,
    swrFetcher,
    { shouldRetryOnError: false }
  );
  // Synchronize checkbox and tags w hen a user is selected/unselected
  const handleNewsUpdatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked, dataset } = e.target; // dataset contains the user name
    const userName = dataset.name || "Unnamed User";

    setNewsUpdates((prev) => {
      const updatedIds = checked
        ? [...prev, id]
        : prev.filter((item) => item !== id);

      // Update tags
      setTags((prevTags) => {
        if (checked) {
          handleTags([...prevTags, { key: id, label: userName }]);

          return [...prevTags, { key: id, label: userName }];
        } else {
          handleTags(prevTags.filter((tag) => tag.key !== id));

          return prevTags.filter((tag) => tag.key !== id);
        }
      });

      handleUsers(updatedIds); // Notify parent of user selection changes
      return updatedIds;
    });
  };

  // Synchronize "Select All" checkbox with all user selections
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setSelectAll(checked);

    if (checked) {
      const allUserIds = TableUsers?.data.map((user) => user.id) || [];
      const allTags =
        TableUsers?.data.map((user) => ({
          key: user.id,
          label: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
        })) || [];

      setNewsUpdates(allUserIds);
      setTags(allTags);
      handleUsers(allUserIds);
      handleTags(allTags);
    } else {
      setNewsUpdates([]);
      setTags([]);
      handleUsers([]);
      handleTags([]);
    }
  };

  // Handle tag removal
  const handleTagsChange = (updatedTags: { key: string; label: string }[]) => {
    const updatedIds = updatedTags.map((tag) => tag.key);
    handleTags(updatedTags);

    setTags(updatedTags);
    setNewsUpdates(updatedIds); // Update selected users
    handleUsers(updatedIds); // Notify parent of user selection changes
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleRecipients = () => {
    if (newsUpdates.length > 0) {
      generateResult("success", "Recipients added");
      handleDrawer();
    } else {
      generateResult("error", "Please select a recipient");
    }
  };

  if (error) {
    const e = parseClientError(error);
    console.log(e);
  }

  const permissions =
    TableUsers?.data.map((user) => (
      <div className={s.input} key={user.id}>
        <input
          type="checkbox"
          id={user.id}
          data-name={`${user.firstName || ""} ${user.lastName || ""}`.trim()} // Pass user name
          checked={newsUpdates.includes(user.id)}
          onChange={handleNewsUpdatesChange}
        />
        <label className={s.labelWrapper} htmlFor={user.id}>
          <span className={s.label}>
            <h5> {(user.firstName || "") + " " + (user.lastName || "")}</h5>
          </span>
        </label>
      </div>
    )) || null;

  const handleCancel = () => {
    setNewsUpdates([]);
    setSelectAll(false);
    setTags([]);
    handleDrawer();
    handleUsers([]);
    handleTags([]);
  };
  console.log(data?.recipients);
  useEffect(() => {
    if (
      data?.recipients &&
      data?.recipients.length > 0 &&
      Array.isArray(data.recipients) &&
      TableUsers?.data
    ) {
      // Filter and map users in TableUsers.docs that match IDs in data.recipients
      const matchedTags = TableUsers.data.filter((user) =>
        data.recipients.map(item=>item.id).includes(user.id)
      ).map((user) => ({
        key: user.id,
        label: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
      }));
      setTags(matchedTags); // Update tags with matched users
      setNewsUpdates(data.recipients.map(item=>item.id)); // Ensure newsUpdates has the IDs
    }
  }, [data?.recipients, TableUsers?.data]);

  console.log(tags);
  return isLoading || error || loading ? (
    <div className={s.spin}>
      <ThreeDots
        visible={true}
        height="50"
        width="50"
        color="#d52b1e"
        radius="9"
        ariaLabel="three-dots-loading"
        wrapperStyle={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      />
    </div>
  ) : (
    <div className={s.wrapper}>
      <div className={s.searchInput}>
        <SearchInput
          style={{ width: "100%", height: "100%" }}
          placeholder="Search"
          handleSearch={handleSearch}
        />
      </div>
      {tags.length > 0 && (
        <div className={s.tags}>
          <Tags initialTags={tags} onTagsChange={handleTagsChange} />
        </div>
      )}
      <div className={s.inputs}>
        <div className={s.input}>
          <input
            id="select"
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          />
          <label htmlFor="select">Select all</label>
        </div>
        {permissions}
      </div>
      <div className={s.addContactBtns}>
        <button className={clsx(s.button, s.cancelBtn)} onClick={handleCancel}>
          Cancel
        </button>
        <button
          className={clsx(s.button, s.addContactBtn)}
          onClick={handleRecipients}
        >
          Save changes
        </button>
      </div>
    </div>
  );
}

export default Recipients;
